import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import toast from 'react-hot-toast';
import './Contact.css';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1 } }),
};

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const createInitialForm = (product = '') => ({
  name: '',
  company: '',
  email: '',
  phone: '',
  country: '',
  product,
  quantity: '',
  buyerType: '',
  message: '',
});

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [form, setForm] = useState(() => createInitialForm(searchParams.get('product') || ''));

  useEffect(() => {
    const p = searchParams.get('product');
    if (p) setForm(f => ({ ...f, product: p }));
  }, [searchParams]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errorMessage) setErrorMessage('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setErrorMessage('');

    const requiredFields = ['name', 'email', 'country', 'product'];
    const hasMissingRequiredField = requiredFields.some(field => !form[field].trim());

    if (hasMissingRequiredField) {
      const message = 'Please fill in all required fields before submitting.';
      setErrorMessage(message);
      toast.error(message);
      return;
    }

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      const message = 'Email service is not configured. Please try again later or contact us on WhatsApp.';
      setErrorMessage(message);
      toast.error(message);
      return;
    }

    setLoading(true);

    const templateParams = {
      to_email: 'info@sanwariyaagroglobal.com',
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      company: form.company.trim(),
      product: form.product.trim(),
      quantity: form.quantity.trim(),
      country: form.country.trim(),
      buyer_type: form.buyerType.trim(),
      message: form.message.trim(),
    };

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, {
        publicKey: EMAILJS_PUBLIC_KEY,
      });

      setSubmitted(true);
      toast.success('Inquiry sent successfully! We\'ll respond within 24 hours.');
    } catch (err) {
      const message = 'Sorry, we could not send your inquiry right now. Please try again or contact us on WhatsApp.';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="section-tag" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--gold-light)' }}>
              Contact Us
            </div>
            <h1 className="section-title" style={{ color: 'var(--white)', fontSize: 'clamp(30px,5vw,52px)' }}>
              Start Your Import Partnership
            </h1>
            <p className="section-sub" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Ready to source premium Indian agro products? We respond to all genuine trade inquiries within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="contact-main">
        <div className="container">
          <div className="contact-grid">
            {/* INFO */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="section-title">Get In Touch</h2>
              <p className="section-sub" style={{ marginBottom: '36px' }}>
                We welcome inquiries from importers, wholesalers, and retail chain distributors worldwide.
              </p>

              {[
                { icon: '📞', label: 'Phone & WhatsApp', value: '+91 95284 80615', href: 'tel:+919528480615' },
                { icon: '✉️', label: 'Email', value: 'info@sanwariyaagroglobal.com', href: 'mailto:info@sanwariyaagroglobal.com' },
                { icon: '📍', label: 'Office & Factory', value: 'C-18, Naveen Galla Mandi Sthal, Amanpur Road, Kasganj-207123, Uttar Pradesh, India', href: null },
                { icon: '💼', label: 'LinkedIn', value: 'linkedin.com/in/sujal-gupta-exports', href: 'https://www.linkedin.com/in/sujal-gupta-exports' },
              ].map((item) => (
                <div key={item.label} className="contact-item">
                  <div className="contact-icon">{item.icon}</div>
                  <div>
                    <div className="contact-label">{item.label}</div>
                    {item.href ? (
                      <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined}
                        rel="noreferrer" className="contact-value contact-link">
                        {item.value}
                      </a>
                    ) : (
                      <div className="contact-value">{item.value}</div>
                    )}
                  </div>
                </div>
              ))}

              <div className="wa-cta">
                <a
                  href="https://wa.me/919528480615?text=Hello%2C%20I%20am%20interested%20in%20your%20products."
                  target="_blank"
                  rel="noreferrer"
                  className="wa-cta-btn"
                >
                  💬 Chat on WhatsApp for Quick Response
                </a>
              </div>
            </motion.div>

            {/* FORM */}
            <motion.div
              className="contact-form-card"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0.1}
              variants={fadeUp}
            >
              {submitted ? (
                <div className="form-success">
                  <div style={{ fontSize: '56px', marginBottom: '16px' }}>✅</div>
                  <h3>Inquiry Received!</h3>
                  <p>Thank you for reaching out. Our team will contact you within 24 hours with pricing and details.</p>
                  <p style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-light)' }}>
                    For urgent queries, WhatsApp us at <strong>+91 95284 80615</strong>
                  </p>
                  <button className="form-submit" style={{ marginTop: '24px', maxWidth: '200px' }} onClick={() => setSubmitted(false)}>
                    Send Another Inquiry
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="form-title">Send Us an Inquiry</h3>
                  <p className="form-sub">Fill in your details and we'll get back with a detailed quote within 24 hours.</p>
                  <form onSubmit={handleSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Full Name *</label>
                        <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Your name" required />
                      </div>
                      <div className="form-group">
                        <label>Company Name</label>
                        <input type="text" name="company" value={form.company} onChange={handleChange} placeholder="Your company" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Email Address *</label>
                        <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" required />
                      </div>
                      <div className="form-group">
                        <label>WhatsApp / Phone</label>
                        <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+1 234 567 8900" />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Country *</label>
                      <input type="text" name="country" value={form.country} onChange={handleChange} placeholder="Your country" required />
                    </div>
                    <div className="form-group">
                      <label>Product of Interest *</label>
                      <select name="product" value={form.product} onChange={handleChange} required>
                        <option value="">Select a product category</option>
                        <optgroup label="Frozen Foods">
                          <option>Frozen Green Peas</option>
                          <option>Frozen Sweet Corn</option>
                          <option>Frozen Mix Veg 3-Way</option>
                          <option>Frozen Mix Veg 4-Way</option>
                          <option>Frozen Mix Veg 5-Way</option>
                          <option>Frozen Mix Veg 6-Way</option>
                        </optgroup>
                        <optgroup label="Organic Spices">
                          <option>Organic Spices (All)</option>
                        </optgroup>
                        <optgroup label="Foxnuts (Makhana)">
                          <option>Plain Foxnuts (Makhana)</option>
                          <option>Flavored Foxnuts (Makhana)</option>
                        </optgroup>
                        <optgroup label="Dehydrated Powders">
                          <option>Onion Powder</option>
                          <option>Garlic Powder</option>
                          <option>Ginger Powder</option>
                          <option>Potato Powder</option>
                        </optgroup>
                        <option>Multiple Products</option>
                      </select>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Quantity Required</label>
                        <input type="text" name="quantity" value={form.quantity} onChange={handleChange} placeholder="e.g. 1000 kg / 1 FCL" />
                      </div>
                      <div className="form-group">
                        <label>Buyer Type</label>
                        <select name="buyerType" value={form.buyerType} onChange={handleChange}>
                          <option value="">Select</option>
                          <option>Wholesaler</option>
                          <option>Retail Chain Distributor</option>
                          <option>Importer / Trader</option>
                          <option>Supermarket / Retailer</option>
                          <option>Food Manufacturer</option>
                          <option>Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Message / Additional Requirements</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Tell us more about your requirements, preferred packaging, delivery terms, or any specific questions..."
                      />
                    </div>
                    {errorMessage && (
                      <p className="form-error" role="alert">
                        {errorMessage}
                      </p>
                    )}
                    <button type="submit" className="form-submit" disabled={loading}>
                      {loading ? 'Sending...' : 'Send Inquiry →'}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
